import { observer } from "mobx-react-lite";
import type { ChangeEvent } from "react";
import React, { forwardRef, useState } from "react";
import * as t from "@rekajs/types";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { ComponentType } from "@repo/editor/types";
import type { ComponentSettings } from "@repo/editor/externalComponents";
import { HiresRepository } from "@repo/fetch-repository/hire";
import { useRouter } from "next/router";
import { useApp } from "@repo/app/useApp";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";

const fileSizeLimit = 5 * 1024 * 1024;
const allowedFileTypes = [
  "application/pdf",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

// TODO: add Props Type:

interface WorkWithUsProps {
  // validations
  validationMessageRequired: string;
  validationMessageEmail: string;
  validationMessageInvalidFile: string;
  validationMessageOneFileAllowed: string;
  validationMessageInvalidTypeFile: string;
  validationMessageFileSize: string;
  // form titles
  firstNameLabel: string;
  lastNameLabel: string;
  emailLabel: string;
  mobileLabel: string;
  fileLabel: string;
  descriptionLabel: string;

  submitButtonText: string;
  resetButtonText: string;
  formTitle: string;

  // success and error messages
  toastSuccessTitle: string;
  toastErrorTitle: string;
  toastSuccessMessage: string;
  toastErrorMessage: string;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- disabling explicit return type for this function
function useFormSchema(props: WorkWithUsProps) {
  return z.object({
    first_name: z.string().min(1, props.validationMessageRequired),
    last_name: z.string().min(1, props.validationMessageRequired),
    email: z
      .string()
      .min(1, props.validationMessageRequired)
      .email(props.validationMessageEmail),
    file: z
      .custom<FileList>((file) => file instanceof FileList, {
        message: props.validationMessageInvalidFile,
      })
      .refine((files) => files.length === 1, {
        message: props.validationMessageOneFileAllowed,
      })
      .refine(
        (files) => files.length > 0 && allowedFileTypes.includes(files[0].type),
        {
          message: props.validationMessageInvalidTypeFile,
        }
      )
      .refine((files) => files.length > 0 && files[0].size <= fileSizeLimit, {
        message: props.validationMessageFileSize,
      }),

    // description: z.string().optional(),
  });
}

export const WorkWithUs = observer(
  forwardRef<HTMLDivElement, WorkWithUsProps>(
    function WorkWithUsComp(props, ref): React.JSX.Element {
      const app = useApp();
      const isEditor = app.rendererMode === "editor";
      const { toast } = useToast();
      const router = useRouter();
      let emailLabel = props.emailLabel;
      let lastNameLabel = props.lastNameLabel;
      let firstNameLabel = props.firstNameLabel;
      let mobileLabel = props.mobileLabel;
      let fileLabel = props.fileLabel;
      let descriptionLabel = props.descriptionLabel;
      let submitButtonText = props.submitButtonText;
      let resetButtonText = props.resetButtonText;
      let formTitle = props.formTitle;
      let toastSuccessTitle = props.toastSuccessMessage;
      let toastErrorTitle = props.toastErrorMessage;
      let toastSuccessMessage = props.toastSuccessMessage;
      let toastErrorMessage = props.toastErrorMessage;

      let validationMessageRequired = props.validationMessageRequired;
      let validationMessageEmail = props.validationMessageEmail;
      let validationMessageInvalidFile = props.validationMessageInvalidFile;
      let validationMessageOneFileAllowed =
        props.validationMessageOneFileAllowed;
      let validationMessageInvalidTypeFile =
        props.validationMessageInvalidTypeFile;
      let validationMessageFileSize = props.validationMessageFileSize;

      if (!props.emailLabel) {
        emailLabel = "email";
      }

      if (!props.firstNameLabel) {
        firstNameLabel = "First Name";
      }

      if (!props.lastNameLabel) {
        lastNameLabel = "Last Name";
      }

      if (!props.mobileLabel) {
        mobileLabel = "Phone";
      }

      if (!props.fileLabel) {
        fileLabel = "file";
      }

      if (!props.descriptionLabel) {
        descriptionLabel = "Description";
      }

      if (!props.submitButtonText) {
        submitButtonText = "Send";
      }

      if (!props.resetButtonText) {
        resetButtonText = "Reset";
      }

      if (!props.formTitle) {
        formTitle = "Work With Us";
      }

      if (!props.toastSuccessTitle) {
        toastSuccessTitle = "success";
      }

      if (!props.toastErrorTitle) {
        toastErrorTitle = "error";
      }

      if (!props.toastSuccessMessage) {
        toastSuccessMessage = "Your message has been sent successfully";
      }

      if (!props.toastErrorMessage) {
        toastErrorMessage = "Failed to send your message";
      }

      if (!props.validationMessageRequired) {
        validationMessageRequired = "required";
      }

      if (props.validationMessageEmail) {
        validationMessageEmail = "email not valid";
      }

      if (!props.validationMessageInvalidFile) {
        validationMessageInvalidFile = "invalid file type";
      }

      if (!props.validationMessageOneFileAllowed) {
        validationMessageOneFileAllowed = "only one file allowed";
      }

      if (!props.validationMessageInvalidTypeFile) {
        validationMessageInvalidTypeFile = "invalid file type";
      }

      if (!props.validationMessageFileSize) {
        validationMessageFileSize = "file size too large";
      }

      if (router.locale === "fa") {
        if (!props.emailLabel) {
          emailLabel = "ایمیل";
        }
        if (!props.firstNameLabel) {
          firstNameLabel = "نام";
        }
        if (!props.lastNameLabel) {
          lastNameLabel = "نام خانوادگی";
        }
        if (!props.mobileLabel) {
          mobileLabel = "موبایل";
        }
        if (!props.fileLabel) {
          fileLabel = "فایل";
        }
        if (!props.descriptionLabel) {
          descriptionLabel = "توضیحات";
        }
        if (!props.submitButtonText) {
          submitButtonText = "ارسال";
        }
        if (!props.resetButtonText) {
          resetButtonText = "ریست";
        }
        if (!props.formTitle) {
          formTitle = "همکاری با ما";
        }
        if (!props.toastSuccessTitle) {
          toastSuccessTitle = "ارسال با موفقیت انجام شد";
        }
        if (!props.toastErrorTitle) {
          toastErrorTitle = "خطا در ارسال";
        }
        if (!props.toastSuccessMessage) {
          toastSuccessMessage = "پیام شما با موفقیت ارسال شد";
        }
        if (!props.toastErrorMessage) {
          toastErrorMessage = "خطا در ارسال پیام شما";
        }
        if (!props.validationMessageRequired) {
          validationMessageRequired = "لطفا این فیلد را پر کنید";
        }
        if (!props.validationMessageEmail) {
          validationMessageEmail = "لطفا ایمیل معتبر وارد کنید";
        }
        if (!props.validationMessageInvalidFile) {
          validationMessageInvalidFile = "لطفا یک فایل معتبر را انتخاب کنید";
        }
        if (!props.validationMessageOneFileAllowed) {
          validationMessageOneFileAllowed = "لطفا یک فایل را انتخاب کنید";
        }
        if (!props.validationMessageInvalidTypeFile) {
          validationMessageInvalidTypeFile =
            "لطفا فایلی با یکی از فرمت های مجاز (PDF, XLSX, DOCX) انتخاب کنید";
        }
        if (!props.validationMessageFileSize) {
          validationMessageFileSize = "حجم فایل نباید بیشتر از 5 مگابایت باشد";
        }
      }

      const [computedFileLabel, setComputedFileLabel] = useState(fileLabel);

      const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0];
        if (file) {
          setComputedFileLabel(file.name); // نام فایل انتخاب شده را تنظیم می‌کند
        } else {
          setComputedFileLabel(fileLabel);
        }
      };

      const formSchema = useFormSchema({
        firstNameLabel: firstNameLabel || "",
        lastNameLabel: lastNameLabel || "",
        emailLabel: emailLabel || "",
        mobileLabel: mobileLabel || "",
        fileLabel: fileLabel || "",
        descriptionLabel: descriptionLabel || "",
        formTitle: formTitle || "",
        toastSuccessTitle,
        toastErrorTitle,
        toastSuccessMessage: toastSuccessMessage || "",
        toastErrorMessage: toastErrorMessage || "",
        validationMessageRequired: validationMessageRequired || "",
        validationMessageEmail: validationMessageEmail || "",
        validationMessageInvalidFile: validationMessageInvalidFile || "",
        validationMessageOneFileAllowed: validationMessageOneFileAllowed || "",
        validationMessageInvalidTypeFile:
          validationMessageInvalidTypeFile || "",
        validationMessageFileSize: validationMessageFileSize || "",
        submitButtonText: submitButtonText || "",
        resetButtonText: resetButtonText || "",
      });
      type FormData = z.infer<typeof formSchema>;

      const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
      } = useForm<FormData>({
        resolver: zodResolver(formSchema),
      });

      const onSubmit = async (data: FormData): Promise<void> => {
        const repository: HiresRepository = new HiresRepository();

        const res = await repository.create(data);
        if (res.succeeded) {
          toast({
            title: toastSuccessTitle,
            description: props.toastSuccessMessage,
            variant: "default",
          });
          reset();
          setComputedFileLabel(fileLabel);
        } else {
          toast({
            title: toastErrorTitle,
            description: props.toastErrorMessage,
            variant: "destructive",
          });
        }
      };

      return (
        <div className="w-full h-full" ref={ref}>
          <div className="mx-auto flex-col lg:flex-row flex items-center justify-between py-10 lg:gap-32">
            <div>
              <h1 className="web-font-helvetica rtl:web-font-doran web-text-[19px] md:web-text-[40px] lg:web-text-[50px] lg:web-font-[100] rtl:lg:web-font-bold text-white uppercase">
                {formTitle}
              </h1>
            </div>
            <div className="w-full flex flex-col items-center justify-center">
              <form
                className="w-full rtl:font-doran font-light rtl:font-medium"
                onSubmit={(e) => {
                  if (isEditor) {
                    e.preventDefault();
                    return;
                  }
                  void handleSubmit(onSubmit)(e);
                }}
              >
                <div className="flex flex-col w-full mt-3">
                  <div className="w-full pt-3 pb-1">
                    <input
                      {...register("first_name")}
                      className="w-full h-full bg-transparent border-none outline-none text-white
                    text-[15px] font-inter placeholder:text-[#ffffff70] tracking-[-0.68px]"
                      id="first_name"
                      placeholder={firstNameLabel}
                      type="text"
                    />
                    {errors.first_name ? (
                      <label
                        className="text-red-500 text-sm mt-1"
                        htmlFor="first_name"
                      >
                        {errors.first_name.message}
                      </label>
                    ) : null}
                  </div>
                  <div className="w-full h-[1px] bg-[#f70] flex-grow-0" />
                </div>

                <div className="flex flex-col w-full mt-3">
                  <div className="w-full pt-3 pb-1">
                    <input
                      {...register("last_name")}
                      className="w-full h-full bg-transparent border-none outline-none text-white
                    text-[15px] font-inter placeholder:text-[#ffffff70] tracking-[-0.68px]"
                      id="last_name"
                      placeholder={lastNameLabel}
                      type="text"
                    />
                    {errors.last_name ? (
                      <label
                        className="text-red-500 text-sm mt-1"
                        htmlFor="last_name"
                      >
                        {errors.last_name.message}
                      </label>
                    ) : null}
                  </div>
                  <div className="w-full h-[1px] bg-[#f70] flex-grow-0" />
                </div>

                <div className="flex flex-col w-full mt-3">
                  <div className="w-full pt-3 pb-1">
                    <input
                      {...register("email")}
                      className="w-full h-full bg-transparent border-none outline-none text-white
                    text-[15px] font-inter placeholder:text-[#ffffff70] tracking-[-0.68px]"
                      id="email"
                      placeholder={emailLabel}
                      type="text"
                    />
                    {errors.email ? (
                      <label
                        className="text-red-500 text-sm mt-1"
                        htmlFor="email"
                      >
                        {errors.email.message}
                      </label>
                    ) : null}
                  </div>
                  <div className="w-full h-[1px] bg-[#f70] flex-grow-0" />
                </div>

                {/* <div className="flex flex-col w-full mt-3">
                  <div className="w-full py-2">
                    <input
                      {...register("mobile")}
                      className="w-full h-full bg-transparent border-none outline-none text-white
                    text-[15px] font-inter font-light placeholder:text-[#ffffff70] tracking-[-0.68px] placeholder:text-left"
                      id="mobile"
                      placeholder={mobileLabel}
                      type="text"
                    />
                    {errors.mobile ? (
                      <label
                        className="text-red-500 text-sm mt-1"
                        htmlFor="mobile"
                      >
                        {errors.mobile.message}
                      </label>
                    ) : null}
                  </div>
                  <div className="w-full h-[1px] bg-[#f70] flex-grow-0" />
                </div> */}

                <div className="flex flex-col w-full mt-3">
                  <div className="w-full pt-3 pb-1">
                    <label
                      className="text-[#ffffff70] cursor-pointer w-full block"
                      htmlFor="file"
                    >
                      {computedFileLabel}
                    </label>
                    <input
                      hidden
                      {...register("file", {
                        onChange: handleFileChange,
                      })}
                      className="w-full h-full bg-transparent border-none outline-none text-white
                    text-[15px] font-inter placeholder:text-[#ffffff70] tracking-[-0.68px] placeholder:text-left"
                      id="file"
                      placeholder={fileLabel}
                      type="file"
                    />
                    {errors.file ? (
                      <label
                        className="text-red-500 text-sm mt-1"
                        htmlFor="file"
                      >
                        {errors.file.message}
                      </label>
                    ) : null}
                  </div>
                  <div className="w-full h-[1px] bg-[#f70] flex-grow-0" />
                </div>

                {/* <div className="flex flex-col w-full mt-3">
                  <div className="w-full py-2">
                    <input
                      {...register("description")}
                      className="w-full h-full bg-transparent border-none outline-none text-white
                    text-[15px] font-inter font-light placeholder:text-[#ffffff70] tracking-[-0.68px] placeholder:text-left"
                      id="email"
                      placeholder={descriptionLabel}
                      type="text"
                    />
                    {errors.description ? (
                      <label
                        className="text-red-500 text-sm mt-1"
                        htmlFor="email"
                      >
                        {errors.description.message}
                      </label>
                    ) : null}
                  </div>
                  <div className="w-full h-[1px] bg-[#f70] flex-grow-0" />
                </div> */}

                <div className="w-full flex items-center justify-start mt-6 gap-4">
                  <Button>{submitButtonText}</Button>
                  <Button
                    onClick={() => {
                      reset();
                      setComputedFileLabel(fileLabel);
                    }}
                    type="button"
                    variant="outline"
                  >
                    {resetButtonText}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      );
    }
  )
) as unknown as ComponentType<WorkWithUsProps>;

export const WorkWithUsSettings: ComponentSettings = {
  name: "EWorkWithUs",
  props: {},
  label: "بخش کار با ما",
  widgets: {
    label: "بخش کار با ما",
    template: () =>
      t.componentTemplate({
        component: t.identifier({ name: "EWorkWithUs", external: true }),
        props: {
          label: t.literal({ value: "بخش کار با ما" }),
        },
      }),
  },
};

WorkWithUs.componentSettings = WorkWithUsSettings;
